/**
 * Balanced row arrangements for the number widget's metric blocks, ported from
 * iOS `MetricGridLayout` (TECH-1986). Rows are balanced with the larger rows
 * first, so 5 metrics render 3,2 rather than 4,1.
 *
 * A number widget with more than 3 metrics needs horizontal space, so past
 * `NUMBER_WIDGET_FULL_ROW_METRIC_THRESHOLD` the widget spans a full feed row
 * and the per-row maximum goes from 3 to 4 — the card's height grows a row at
 * a time instead of one row per three metrics.
 */
export const NUMBER_WIDGET_FULL_ROW_METRIC_THRESHOLD = 4

export function getNumberWidgetMaxPerRow(count: number): number {
  return count >= NUMBER_WIDGET_FULL_ROW_METRIC_THRESHOLD ? 4 : 3
}

/**
 * The number of metrics in each row, in top-to-bottom order.
 *
 * | n  | sizes      |
 * |----|------------|
 * | 1  | 1          |
 * | 2  | 2          |
 * | 3  | 3          |
 * | 4  | 4          |
 * | 5  | 3,2        |
 * | 6  | 3,3        |
 * | 7  | 4,3        |
 * | 8  | 4,4        |
 * | 9  | 3,3,3      |
 * | 10 | 4,3,3      |
 * | 11 | 4,4,3      |
 * | 12 | 4,4,4      |
 * | 13+| max 4/row  |
 */
export function getMetricGridRowSizes({ count, maxPerRow }: { count: number; maxPerRow: number }): number[] {
  if (count <= 0) return []
  const rowCount = Math.ceil(count / maxPerRow)
  const base = Math.floor(count / rowCount)
  const remainder = count % rowCount
  return Array.from({ length: rowCount }, (_, index) => base + (index < remainder ? 1 : 0))
}

/** Chunks `items` into rows matching `getMetricGridRowSizes`. */
export function getMetricGridRows<T>(items: T[]): T[][] {
  const sizes = getMetricGridRowSizes({ count: items.length, maxPerRow: getNumberWidgetMaxPerRow(items.length) })
  const rows: T[][] = []
  let start = 0
  for (const size of sizes) {
    rows.push(items.slice(start, start + size))
    start += size
  }
  return rows
}
